/// <amd-module name="VSS/Diagnostics/Dialog/DiagnosticsScriptsGraph" />
import * as React from "react";
import { ContributionLink, ContributionNode } from "VSS/Diagnostics/Dialog/DiagnosticsDialog.types";
import { ContentType, IVssContentService } from "VSS/Platform/Content";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { drawDependencyDiagram } from "./DependencyDiagram";

export interface IDiagnosticsScriptGraphProps extends IVssComponentProperties {
    contributions: ContributionNode[];
    relationships: ContributionLink[];
}

export class DiagnosticsScriptGraph extends VssComponent<IDiagnosticsScriptGraphProps> {
    private element = React.createRef<HTMLDivElement>();

    constructor(props: IDiagnosticsScriptGraphProps, context: IVssComponentContext) {
        super(props, context);
    }

    public render(): JSX.Element {
        return (
            <div className="diag-dialog-scripts-graph flex-grow flex-column">
                <div className="flex-row flex-center">
                    <div className="flex-grow">
                        Hover over any segment to highlight the modules that have it as a dependency. Click any segment to remove it from the chart.
                    </div>
                    <Button onClick={this._onResetClick}>Reset</Button>
                </div>
                <div className="flex-grow" ref={this.element} />
            </div>
        );
    }

    public componentDidMount(): void {
        super.componentDidMount();

        this._drawChart();
    }

    private _onResetClick = (): void => {
        this._drawChart();
    };

    private _drawChart(): void {
        if (!this.element.current) {
            return;
        }

        const contentService = this.context.pageContext.getService<IVssContentService>("IVssContentService");

        const { contributions, relationships } = this.props;

        const contentContributionType = "ms.vss-web.content";
        const filteredContributions = contributions.filter(x => x.type === contentContributionType);
        const filteredRelationships = relationships
            .filter(
                x =>
                    (x.source as ContributionNode).type === contentContributionType &&
                    filteredContributions.some(c => c.id === (x.source as ContributionNode).id) &&
                    (x.target as ContributionNode).type === contentContributionType &&
                    filteredContributions.some(c => c.id === (x.target as ContributionNode).id)
            )
            .map(l => {
                const { source, target } = l;

                // Switch relationship direction for dependency graph
                return {
                    ...l,
                    target: source,
                    source: target
                };
            });

        // Build connections array
        filteredContributions.sort((a, b) => a.id.localeCompare(b.id, navigator.language));

        const connections: [string, string][] = [];
        for (let x = 0; x < filteredContributions.length; ++x) {
            const xNode = filteredContributions[x];
            for (let y = 0; y < filteredContributions.length; ++y) {
                const yNode = filteredContributions[y];

                if (
                    filteredRelationships.some(c => (c.source as ContributionNode).id === xNode.id && (c.target as ContributionNode).id === yNode.id)
                ) {
                    connections.push([xNode.id, yNode.id]);
                }
            }
        }

        const contributionIds = filteredContributions.map(c => c.id);
        const fileSizes = contributionIds.map(contributionId => {
            const info = contentService.getContributionContentLengths(contributionId);
            return (info && info[ContentType.javaScript]) || 10;
        });
        drawDependencyDiagram(this.element.current, contributionIds, fileSizes, connections);
    }
}
